import { Controller } from "@hotwired/stimulus"
import "../admin/rhino_setup"
import "rhino-editor"

// Éditeur de texte riche des formulaires d'administration : Rhino Editor (TipTap),
// branché sur Action Text. Voir admin/shared/_rich_text_field.html.erb.
//
// Ce contrôleur gère :
// - les boutons ajoutés à la barre d'outils (titres h3/h4, souligné, alignement,
//   ligne de séparation, tableaux), qui appellent les commandes de l'éditeur ;
// - les fichiers joints (images, PDF, documents...) : ils sont envoyés à Rails
//   (Admin::RichTextUploadsController), qui renvoie l'identifiant (sgid) à placer
//   dans le texte. On n'utilise pas l'envoi direct d'Action Text : le stockage
//   (MinIO) n'est pas joignable depuis le navigateur.
const MAX_FILE_SIZE = 10 * 1024 * 1024

// Commande de chaque bouton : (chaîne TipTap) => chaîne.
const COMMANDS = {
  heading3: (chain) => chain.toggleHeading({ level: 3 }),
  heading4: (chain) => chain.toggleHeading({ level: 4 }),
  underline: (chain) => chain.toggleUnderline(),
  alignLeft: (chain) => chain.setTextAlign("left"),
  alignCenter: (chain) => chain.setTextAlign("center"),
  alignRight: (chain) => chain.setTextAlign("right"),
  alignJustify: (chain) => chain.setTextAlign("justify"),
  horizontalRule: (chain) => chain.setHorizontalRule(),
  insertTable: (chain) => chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true }),
  addRowAfter: (chain) => chain.addRowAfter(),
  addColumnAfter: (chain) => chain.addColumnAfter(),
  deleteRow: (chain) => chain.deleteRow(),
  deleteColumn: (chain) => chain.deleteColumn(),
  toggleHeaderRow: (chain) => chain.toggleHeaderRow(),
  mergeOrSplit: (chain) => chain.mergeOrSplit(),
  deleteTable: (chain) => chain.deleteTable()
}

// Ce qui rend un bouton « actif » (enfoncé) selon la position du curseur.
const ACTIVE = {
  heading3: (editor) => editor.isActive("heading", { level: 3 }),
  heading4: (editor) => editor.isActive("heading", { level: 4 }),
  underline: (editor) => editor.isActive("underline"),
  alignLeft: (editor) => editor.isActive({ textAlign: "left" }),
  alignCenter: (editor) => editor.isActive({ textAlign: "center" }),
  alignRight: (editor) => editor.isActive({ textAlign: "right" }),
  alignJustify: (editor) => editor.isActive({ textAlign: "justify" })
}

export default class extends Controller {
  static targets = ["editor", "button", "tableTool", "error"]
  static values = { uploadUrl: String }

  connect() {
    this.accept = this.accept.bind(this)
    this.upload = this.upload.bind(this)
    this.refresh = this.refresh.bind(this)
    this.element.addEventListener("rhino-file-accept", this.accept)
    this.element.addEventListener("rhino-attachment-add", this.upload)
    this.constructor.refreshEvents.forEach((name) => this.element.addEventListener(name, this.refresh))
  }

  disconnect() {
    this.element.removeEventListener("rhino-file-accept", this.accept)
    this.element.removeEventListener("rhino-attachment-add", this.upload)
    this.constructor.refreshEvents.forEach((name) => this.element.removeEventListener(name, this.refresh))
  }

  static refreshEvents = ["rhino-initialize", "rhino-update", "rhino-selection-change", "rhino-focus"]

  get editor() {
    return this.editorTarget.editor
  }

  // Bouton ajouté à la barre d'outils : data-rich-text-command-param="insertTable".
  run(event) {
    event.preventDefault()
    const command = COMMANDS[event.params.command]
    if (!command || !this.editor) return

    command(this.editor.chain().focus()).run()
    this.refresh()
  }

  // État des boutons ajoutés : enfoncé si le format est actif ; les outils de
  // tableau n'apparaissent que lorsque le curseur est dans un tableau.
  refresh() {
    const editor = this.editor
    if (!editor) return

    this.buttonTargets.forEach((button) => {
      const active = ACTIVE[button.dataset.richTextCommandParam]
      if (active) button.setAttribute("aria-pressed", active(editor) ? "true" : "false")
    })
    const inTable = editor.isActive("table")
    this.tableToolTargets.forEach((tool) => { tool.hidden = !inTable })
  }

  accept(event) {
    this.showError("")
    if (event.file.size > MAX_FILE_SIZE) {
      event.preventDefault()
      this.showError("Le fichier ne doit pas dépasser 10 Mo.")
    }
  }

  async upload(event) {
    const attachment = event.attachment
    if (!attachment.file) return // fichier déjà enregistré (réouverture du formulaire)

    event.preventDefault() // pas d'envoi direct vers le stockage : on passe par Rails
    const body = new FormData()
    body.append("file", attachment.file)
    try {
      const response = await fetch(this.uploadUrlValue, {
        method: "POST",
        body,
        headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content, Accept: "application/json" }
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || "Envoi refusé.")
      attachment.setAttributes({ sgid: data.sgid, url: data.url })
      attachment.setUploadProgress(100)
    } catch (error) {
      this.editor?.commands.undo()
      this.showError(`Le fichier n'a pas pu être envoyé : ${error.message}`)
    }
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.hidden = !message
  }
}
